import { ReactNode, useCallback, useEffect } from 'react';

import { useContextualSurface } from 'approot/shared/ally/contextual.hooks';
import { useActivityBuilderPresentContext } from 'approot/activity-builder/present/activity-builder-present.context';
import { ESCAPE } from 'approot/shared/ally/keyboard.hooks';
import * as Styled from './settings-present-component-menu.style';

export const SETTINGS_MENU_ID = 'present-inline-settings';

type Props = {
  children: ReactNode;
  isActive: boolean;
  menuItems?: ReactNode;
};

const SettingsPresentComponentMenu = ({
  children,
  isActive,
  menuItems,
}: Props) => {
  const { activeComponent, setActiveComponent } =
    useActivityBuilderPresentContext();

  const { styles, attributes, setReferenceElement, setPopperElement } =
    useContextualSurface(isActive, () => {}, {
      offset: [6, 0],
      placement: 'top-start',
      fallbackPlacements: ['top-start'],
      strategy: 'fixed',
    });

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === ESCAPE) {
        e.stopPropagation();
        e.preventDefault();
        setActiveComponent(undefined);
      }
    },
    [setActiveComponent]
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!activeComponent) return null;

      // handle ignoring clicks inside the menu control
      const menuInnerElement = document.getElementById('menu-inner');
      if (
        menuInnerElement?.contains(event.target as HTMLElement) ||
        (event.target as HTMLElement)?.dataset?.tts
      ) {
        return false;
      }

      // handle click events outside this component
      const el = document.getElementById(`${activeComponent.id}-subactivity`);
      if (isActive && el && !el.contains(event.target as Node)) {
        setActiveComponent(undefined);
      }
    };

    if (isActive) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isActive, activeComponent, setActiveComponent]);

  return (
    <Styled.ComponentMenuWrapper
      ref={setReferenceElement}
      id={SETTINGS_MENU_ID}
    >
      {children}
      {isActive && (
        <Styled.ComponentMenuInner
          id="menu-inner"
          ref={el => setPopperElement(el)}
          role="dialog"
          tabIndex={-1}
          style={styles.popper}
          {...attributes.popper}
          onKeyDown={handleKeyDown}
        >
          {menuItems}
        </Styled.ComponentMenuInner>
      )}
    </Styled.ComponentMenuWrapper>
  );
};

export default SettingsPresentComponentMenu;
